import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/opt/build/repo/node_modules/gatsby-theme-carbon/src/templates/Default.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h2>{`What is artificial intelligence?`}</h2>
    <p>{`Artificial intelligence leverages computers and machines to mimic the problem-solving and decision-making capabilities of the human mind. AI combines computer science and robust datasets to enable problem-solving. AI developers take the models that data scientists create and make them into deployable models that can be used in applications.`}</p>
    <h2>{`AI and the cloud`}</h2>
    <p>{`Integrating AI and machine learning technologies with cloud environments is an increasingly common scenario, driven by use of microservices and the need to scale rapidly. Developers are faced with the challenge to not only build machine learning applications, but to ensure that they run well in production in cloud-native and hybrid cloud environments. `}</p>
    <h2>{`Solution sketch`}</h2>
    <p>{`When developing AI-powered services and applications that run in cloud environments, there is a vast array of development areas to consider including:`}</p>
    <ul>
      <li parentName="ul">{`Datasets`}</li>
      <li parentName="ul">{`Work with Data: Wrangling, Cleaning, Analysis, and Computation`}</li>
      <li parentName="ul">{`Data Visualization`}</li>
      <li parentName="ul">{`Model Development `}</li>
      <li parentName="ul">{`Model Deployment`}</li>
    </ul>
    <h2>{`Key open source projects`}</h2>
    <h3>{`Datasets`}</h3>
    <p>{`Refer `}<a parentName="p" {...{
        "href": "https://opensource.org/licenses"
      }}>{`here`}</a>{` to learn about licenses.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}></th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Source`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Governing body`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://github.com/IBM/Project_CodeNet"
            }}>{`Project CodeNet`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://github.com/IBM/Project_CodeNet"
            }}>{`GH repo`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`LF AI & Data`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://registry.opendata.aws/"
            }}>{`Registry of Open Data on AWS`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://github.com/awslabs/open-data-registry/"
            }}>{`GH repo`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`None`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://github.com/awesomedata/awesome-public-datasets"
            }}>{`Awesome Public Datasets`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://github.com/awesomedata/awesome-public-datasets"
            }}>{`GH repo`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://data.worldbank.org/"
            }}>{`World Bank Open Data`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://www.who.int/data/gho/"
            }}>{`WHO Open Data`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://www.google.com/publicdata/directory"
            }}>{`Google Public Data Explorer`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "http://open-data.europa.eu/en/data/"
            }}>{`European Union Open Data Portal`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "http://www.census.gov/data.html"
            }}>{`U.S.Census Bureau`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://www.data.gov/"
            }}>{`Data.gov`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://www.yelp.com/dataset"
            }}>{`Yelp Open Dataset`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://data.unicef.org/"
            }}>{`UNICEF`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
      </tbody>
    </table>
    <h3>{`Work with Data - Wrangling, Cleaning, Analysis, Computation`}</h3>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}></th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Source`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Governing body`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Get Started guide`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://pandas.pydata.org/"
            }}>{`Pandas`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://github.com/pandas-dev/pandas/"
            }}>{`GH Repo`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://pandas.pydata.org/docs/"
            }}>{`Guide`}</a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://numpy.org/"
            }}>{`NumPy`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://github.com/numpy"
            }}>{`GH Repo`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://numpy.org/doc/stable/"
            }}>{`Guide`}</a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://www.scipy.org/"
            }}>{`SciPy`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://github.com/scipy/scipy"
            }}>{`GH Repo`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://www.scipy.org/docs.html"
            }}>{`Guide`}</a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://www.crummy.com/software/BeautifulSoup/"
            }}>{`BeautifulSoup`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://code.launchpad.net/beautifulsoup"
            }}>{`Source`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://www.crummy.com/software/BeautifulSoup/bs4/doc/"
            }}>{`Guide`}</a></td>
        </tr>
      </tbody>
    </table>
    <h3>{`Data Visualization`}</h3>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}></th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Source`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Governing body`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Get Started guide`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://matplotlib.org/"
            }}>{`Matplotlib`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://github.com/matplotlib/matplotlib/"
            }}>{`GH Repo`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://matplotlib.org/stable/contents.html"
            }}>{`Guide`}</a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://seaborn.pydata.org/"
            }}>{`Seaborn`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://github.com/seaborn"
            }}>{`GH Repo`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://seaborn.pydata.org/tutorial.html"
            }}>{`Guide`}</a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://plotly.com/python/"
            }}>{`Plotly`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://github.com/plotly/plotly.py"
            }}>{`GH Repo`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://plotly.com/python/getting-started/"
            }}>{`Guide`}</a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "http://docs.bokeh.org/en/latest/"
            }}>{`Bokeh`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://github.com/bokeh/bokeh"
            }}>{`GH Repo`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "http://docs.bokeh.org/en/latest/docs/user_guide.html"
            }}>{`Guide`}</a></td>
        </tr>
      </tbody>
    </table>
    <h3>{`Model Development`}</h3>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}></th>
          <th parentName="tr" {...{
            "align": null
          }}>{`GitHub repo`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Governing body`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Get Started guide`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://www.tensorflow.org/"
            }}>{`Tensorflow`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://github.com/tensorflow/tensorflow"
            }}>{`GH repo`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`TBD`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://www.tensorflow.org/learn"
            }}>{`TensorFlow Get Started`}</a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://keras.io/"
            }}>{`Keras`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://github.com/keras-team/keras"
            }}>{`GH repo`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`TBD`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://keras.io/getting_started"
            }}>{`Keras Get Started`}</a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://pytorch.org/"
            }}>{`PyTorch`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://github.com/pytorch/pytorch"
            }}>{`GH repo`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`TBD`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://pytorch.org/get-started/locally/"
            }}>{`https://pytorch.org/get-started/locally/`}</a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://www.nltk.org/"
            }}>{`NLTK`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://github.com/nltk/nltk"
            }}>{`GH Repo`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://www.nltk.org/api/nltk.html"
            }}>{`Guide`}</a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://radimrehurek.com/gensim/"
            }}>{`Gensim`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://github.com/RaRe-Technologies/gensim"
            }}>{`GH Repo`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://radimrehurek.com/gensim/auto_examples/index.html#documentation"
            }}>{`Guide`}</a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://scikit-learn.org/stable/"
            }}>{`Scikit-Learn`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://github.com/scikit-learn/scikit-learn"
            }}>{`GH Repo`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://scikit-learn.org/stable/getting_started.html"
            }}>{`Guide`}</a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://www.statsmodels.org/stable/index.html"
            }}>{`Statsmodels`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://github.com/statsmodels/statsmodels"
            }}>{`GH Repo`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://www.statsmodels.org/stable/gettingstarted.html"
            }}>{`Guide`}</a></td>
        </tr>
      </tbody>
    </table>
    <h3>{`Model Deployment`}</h3>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}></th>
          <th parentName="tr" {...{
            "align": null
          }}>{`GitHub repo`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Governing body`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Get Started guide`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://www.docker.com/"
            }}>{`Docker`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://github.com/docker"
            }}>{`GH Repo`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://docs.docker.com/"
            }}>{`Guide`}</a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://kubernetes.io/"
            }}>{`Kubernetes`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://github.com/kubernetes/kubernetes"
            }}>{`GH Repo`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://kubernetes.io/docs/home/"
            }}>{`Guide`}</a></td>
        </tr>
      </tbody>
    </table>
    <h2>{`Cloud comparision`}</h2>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}></th>
          <th parentName="tr" {...{
            "align": null
          }}>{`IBM Cloud`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`GCP`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`AWS`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Azure`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Machine Learning Frameworks`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`TBD`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`TBD`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`TBD`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`TBD`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Datasets`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`1.18 - 1.21`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`TBD`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`TBD`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`TBD`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Deep learning libraries`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`TBD`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`TBD`}</td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
      </tbody>
    </table>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      